import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './style.less';
import { Button, Dimmer, Image, Loader, Modal, Segment, Table } from 'semantic-ui-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { fetchAllCommunityNews } from '../../services/CommunityNews';
import { CommunityNewsType } from '../../types/CommunityNews';
import moment from 'moment-timezone';
import { getStandaloneSignageImage } from '../../services/ImageUploader';
import useMediaQuery from '../../hooks/useMediaQuery';
import CompactMonthCalendar, {
    CompactCalendarEvent,
} from '../../components/CompactMonthCalendar';
import { useFacility } from '../../contexts/facility/FacilityContext';

interface DateRange {
    startDate: string;
    endDate: string;
}

/** Switch from FullCalendar to CompactMonthCalendar at this width and below. */
const COMPACT_CALENDAR_QUERY = '(max-width: 1024px)';

/** Facility wall-clock as a local Date (FullCalendar + compact both bucket locally). */
const toFacilityLocalDate = (value: string, facilityTimezone: string): Date => {
    const wallClock = moment.tz(value, facilityTimezone);
    if (!wallClock.isValid()) return new Date(value);
    return new Date(wallClock.format('YYYY-MM-DDTHH:mm:ss'));
};

const CommunityNews = () => {
    const isCompact = useMediaQuery(COMPACT_CALENDAR_QUERY);
    const { facilityTimezone } = useFacility();
    const [loading, setLoading] = useState<boolean>(false);
    const [communityNews, setCommunityNews] = useState<CommunityNewsType[]>([]);
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({} as DateRange);
    const [selectedEvent, setSelectedEvent] = useState<Partial<CommunityNewsType> | null>(null);
    const [detailedModal, setDetailedModal] = useState<boolean>(false);
    const [modalLoading, setModalLoading] = useState<boolean>(false);
    const [selectedEventImage, setSelectedEventImage] = useState<string>('');
    /** Month on display, owned here so it survives the compact/desktop swap. */
    const [viewMonth, setViewMonth] = useState(() => moment().startOf('month').toDate());

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only allows one resident to be linked to one account

    useEffect(() => {
        if (selectedDateRange.startDate && selectedDateRange.endDate)
            listCommunityNews();
    }, [selectedDateRange]);

    const listCommunityNews = async () => {
        try {
            setLoading(true);
            const filter = {
                startDate: { $lt: selectedDateRange.endDate },
                endDate: { $gte: selectedDateRange.startDate },
            };
            const news = await fetchAllCommunityNews(
                correspondingSpeak2Resident.Facility,
                filter
            );
            setCommunityNews(news);
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Community News');
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = useCallback(({ start, end }) => {
        const startDateTimeString = moment(start).format('YYYY-MM-DD');
        const endDateTimeString = moment(end).format('YYYY-MM-DD');
        setSelectedDateRange({
            startDate: startDateTimeString,
            endDate: endDateTimeString,
        });
        // Both views report the Sunday-aligned grid, whose 8th day always lands in the
        // month on display. Holding it here keeps the month stable when the breakpoint
        // swaps one calendar for the other.
        const anchor = moment(start).add(7, 'days');
        const nextViewMonth = new Date(anchor.year(), anchor.month(), 1);
        setViewMonth((prev) =>
            prev.getTime() === nextViewMonth.getTime() ? prev : nextViewMonth,
        );
    }, []);

    const openNewsDetail = async (payload: {
        title: string;
        start: Date;
        end: Date;
        id: string;
        signageImageId?: string;
        facilityId?: string;
    }) => {
        try {
            setModalLoading(true);
            setDetailedModal(true);
            const id = payload.signageImageId;
            const facilityId = payload.facilityId;
            if (payload.signageImageId) {
                const url = await getStandaloneSignageImage({
                    facilityId: facilityId,
                    contentType: 'communityMessages',
                    contentId: id,
                });
                setSelectedEventImage(url);
            } else {
                setSelectedEventImage('');
            }
            setSelectedEvent({
                name: payload.title,
                startDate: payload.start as any,
                endDate: (payload.end || payload.start) as any,
                _id: payload.id,
            });
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Community News image');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEventClick = async (info: any) => {
        await openNewsDetail({
            title: info.event.title,
            start: info.event.start,
            end: info.event.end || info.event.start,
            id: info.event.id,
            signageImageId: info.event._def.extendedProps.signageImageId,
            facilityId: info.event._def.extendedProps.facilityId,
        });
    };

    const handleCompactEventClick = async (event: CompactCalendarEvent) => {
        await openNewsDetail({
            title: event.title,
            start: event.start,
            end: event.end || event.start,
            id: event.id,
            signageImageId: event.meta && event.meta.signageImageId,
            facilityId: event.meta && event.meta.facilityId,
        });
    };

    const closeDetailedModal = () => {
        setDetailedModal(false);
        setSelectedEvent(null);
        setSelectedEventImage('');
    };

    const fullCalendarEvents = useMemo(() => {
        if (!facilityTimezone) return [];
        return communityNews.map((event) => ({
            title: event.name,
            start: toFacilityLocalDate(event.startDate, facilityTimezone),
            end: toFacilityLocalDate(event.endDate, facilityTimezone),
            signageImageId: event.signageImageId || '',
            facilityId: event.Facility || '',
            id: event._id,
        }));
    }, [communityNews, facilityTimezone]);

    // Derived from the same array FullCalendar renders, so both views agree on the
    // event set; only the presentation differs.
    const compactEvents: CompactCalendarEvent[] = useMemo(() => {
        if (!isCompact) return [];
        return fullCalendarEvents.map((event) => {
            const spansMultipleDays = !moment(event.start).isSame(event.end, 'day');
            return {
                occurrenceKey: event.id || `${event.title}-${event.start.getTime()}`,
                id: event.id || '',
                title: event.title || '',
                start: event.start,
                end: event.end,
                // News items are date spans rather than timed events, so the card
                // shows the span instead of a time column.
                subtitle: spansMultipleDays
                    ? `${moment(event.start).format('MMM D')} – ${moment(event.end).format('MMM D')}`
                    : moment(event.start).format('MMM D, YYYY'),
                meta: {
                    signageImageId: event.signageImageId,
                    facilityId: event.facilityId,
                },
            };
        });
    }, [isCompact, fullCalendarEvents]);

    return (
        <div>
            {isCompact ? (
                <div className="neighbors-compact-calendar-page">
                    <CompactMonthCalendar
                        events={compactEvents}
                        initialMonth={viewMonth}
                        onDatesSet={handleMonthChange}
                        onEventClick={handleCompactEventClick}
                        loading={loading}
                        itemNoun="news"
                        itemNounPlural="news"
                    />
                </div>
            ) : (
                <div className="community-news-full-calendar">
                    <Segment className="community-news-full-calendar-card">
                        <div className="community-news-calendar-container">
                            <Dimmer active={loading} inverted>
                                <Loader content="Loading" />
                            </Dimmer>
                            <FullCalendar
                                plugins={[dayGridPlugin, interactionPlugin]}
                                initialView="dayGridMonth"
                                initialDate={viewMonth}
                                headerToolbar={{
                                    right: 'today prev,next',
                                }}
                                buttonText={{ today: 'Today' }}
                                dayHeaderFormat={{ weekday: 'long' }}
                                events={fullCalendarEvents}
                                datesSet={handleMonthChange}
                                eventClick={handleEventClick}
                                showNonCurrentDates={false}
                            />
                        </div>
                    </Segment>
                </div>
            )}
            <Modal open={detailedModal} onClose={closeDetailedModal}>
                <Dimmer active={modalLoading} inverted>
                    <Loader />
                </Dimmer>
                <Modal.Header style={{ textAlign: "center" }}>Community News details</Modal.Header>
                <Modal.Content image>
                    <Modal.Description style={{ width: '100%' }}>
                        <Table>
                            <Table.Body>
                                {selectedEvent && selectedEvent.startDate ? (
                                    <Table.Row>
                                        <Table.Cell>Start Date:  </Table.Cell>
                                        <Table.Cell>
                                            {moment(selectedEvent.startDate).format('MMM D, YYYY')}
                                            {' ('}
                                            {moment(selectedEvent.startDate).fromNow()}
                                            {') '}
                                        </Table.Cell>
                                    </Table.Row>
                                ) : (
                                    ''
                                )}
                            </Table.Body>
                            <Table.Body>
                                {selectedEvent && selectedEvent.endDate ? (
                                    <Table.Row>
                                        <Table.Cell>End Date:  </Table.Cell>
                                        <Table.Cell>
                                            {moment(selectedEvent.endDate).format('MMM D, YYYY')}
                                            {' ('}
                                            {moment(selectedEvent.endDate).fromNow()}
                                            {') '}
                                        </Table.Cell>
                                    </Table.Row>
                                ) : (
                                    ''
                                )}
                            </Table.Body>
                            <Table.Header>
                                <Table.Row>
                                    <Table.Cell>Text:  </Table.Cell>
                                    <Table.Cell>{(selectedEvent && selectedEvent.name) || ''}</Table.Cell>
                                </Table.Row>
                            </Table.Header>
                        </Table>
                        {selectedEventImage && <Image style={{ margin: "auto" }} src={selectedEventImage} size='medium' />}
                    </Modal.Description>
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={closeDetailedModal}>
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default CommunityNews;
